import { useCallback } from 'react';

import { useStateWithCallback } from '.';

interface useDynamicChargebeeLinkInput {
    /**
     * Initial state of the link configuration
     */
    initialLinkConfig: LicenseLinkConfig;
}

interface useDynamicChargebeeLinkResult {
    /**
     * Current state of the link configuration
     */
    linkConfig: LicenseLinkConfig;
    /**
     * Method for updating the plan data
     */
    updateChargebeePlanData: UpdateChargebeePlanData;
}

/**
 * Controls the state for a Chargebee link. When the state is updated, it runs
 * `Chargebee.registerAgain()` to alert the Chargebee script that the <a> element's data has
 * changed.
 * @param input - see {@link useDynamicChargebeeLinkInput}
 * @returns a {@link useDynamicChargebeeLinkResult}
 */
export const useDynamicChargebeeLink = ({
    initialLinkConfig,
}: useDynamicChargebeeLinkInput): useDynamicChargebeeLinkResult => {
    const [linkConfig, setLinkConfig] = useStateWithCallback(
        initialLinkConfig,
        (): void => {
            const Chargebee =
                // eslint-disable-next-line
                // @ts-ignore
                window?.Chargebee ?? window?.parent?.window?.Chargebee ?? null;

            if (Chargebee) {
                Chargebee.registerAgain();
            }
        },
    );

    const updateChargebeePlanData = useCallback(
        (key: string, nextValue: string): void => {
            setLinkConfig(
                (prev: LicenseLinkConfig): LicenseLinkConfig => ({
                    ...prev,
                    data: prev.data.map(
                        (linkData: LicenseLinkData): LicenseLinkData => {
                            if (linkData[0] === key) {
                                return [key, nextValue];
                            }

                            return linkData;
                        },
                    ),
                }),
            );
        },
        [],
    );

    return { linkConfig, updateChargebeePlanData };
};
